import { makeStyles } from "tss-react/mui";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { MiTHEME } from "@miplatform/theme";
import { hoursToMinutes, toFormattedDate } from "@miplatform/utils";
import { Box, Tooltip, Typography } from "@mui/material";
import { MiBadge } from "../MiBadge/MiBadge";

const BadgeStatusStyles = ({ scheduledDate }: { scheduledDate?: string }) => {
  if (!scheduledDate) {
    return {
      label: "Not Scheduled",
      color: MiTHEME.colors.black.main,
      backgroundColor: MiTHEME.colors.grey.lightAccent,
      borderColor: MiTHEME.colors.grey.medium,
    };
  }

  return {
    label: "Scheduled",
    color: MiTHEME.colors.info.main,
    backgroundColor: MiTHEME.colors.info.light,
    borderColor: MiTHEME.colors.info.main,
  };
};

export const MiWorkOrderTooltip = ({
  addressLine1,
  builderName,
  city,
  regionCode,
  communityName,
  scheduledDate,
  dueDate,
  completeDate,
  duration,
  completedSteps = 0,
  totalSteps = 0,
  status,
  templateName,
}: {
  addressLine1?: string;
  builderName?: string;
  city?: string;
  regionCode?: string;
  communityName?: string;
  scheduledDate?: string;
  dueDate?: string;
  completeDate?: string;
  duration?: number;
  completedSteps?: number;
  totalSteps?: number;
  status?: string;
  templateName?: string;
}) => {
  const { classes } = useStyles();

  const addressTitle = `${addressLine1} | ${builderName}`;
  const addressInfo = `${city}, ${regionCode}  |  ${communityName}  |  ${regionCode}`;

  const {
    label: badgeLabel,
    color: badgeColor,
    backgroundColor: badgeBackgroundColor,
  } = BadgeStatusStyles({ scheduledDate });

  return (
    <Box className={classes.popperContainer} data-testid="work-order-tooltip">
      <Box className={classes.popperHeader} data-testid="popper-header">
        <Typography className={classes.attemptTypeText}>
          {templateName}
        </Typography>
        <Box className={classes.attemptStatusContainer}>
          <MiBadge
            className={classes.attemptStatus}
            text={badgeLabel}
            data-testid="attempt-status"
            color={badgeColor}
            backgroundColor={badgeBackgroundColor}
            borderColor={badgeBackgroundColor}
          />
        </Box>
      </Box>
      <Box className={classes.addressContainer}>
        <Box className={classes.popperAddress}>
          <Tooltip title={addressLine1}>
            <Box height={11}>
              <Typography className={classes.addressInfo1} noWrap>
                {addressTitle}
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title={addressInfo}>
            <Box className={classes.secondaryPopperAddress}>
              <Typography className={classes.addressInfo2} noWrap>
                {addressInfo}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Scheduled Date</Typography>
          <Typography className={classes.addressInfo1}>
            {toFormattedDate(scheduledDate, DATE_TIME_FORMATS.FORMAT_MM_DD_YY)}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.popperTimeSection}>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Due Date</Typography>
          <Typography className={classes.timeText}>
            {toFormattedDate(dueDate, DATE_TIME_FORMATS.FORMAT_MM_DD_YY)}
          </Typography>
        </Box>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Complete Date</Typography>
          <Typography className={classes.timeText}>
            {toFormattedDate(completeDate, DATE_TIME_FORMATS.FORMAT_MM_DD_YY)}
          </Typography>
        </Box>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Duration</Typography>
          <Box className={classes.flexRow}>
            <Typography className={classes.timeText} data-testid="actual-timer">
              {duration ? hoursToMinutes(duration) : "--"}min
            </Typography>
          </Box>
        </Box>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Steps</Typography>
          <Box className={classes.flexRow}>
            <Typography className={classes.timeText} data-testid="actual-timer">
              {`${completedSteps}/${totalSteps}`}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  popperContainer: {
    width: 300,
    height: "100%",
    borderRadius: 6,
    backgroundColor: theme.palette.common.white,
    overflow: "hidden",
  },
  popperHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxSizing: "content-box",
    borderRadius: "6px 6px 0px 0px",
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    height: 16,
  },
  issueNavigation: {
    display: "flex",
    justifyContent: "space-between",
  },
  attemptTypeText: {
    fontWeight: theme.fontWeight.regular,
    fontSize: theme.fontSize.medium,
  },
  addressContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
  },
  popperAddress: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    height: 43,
    padding: theme.spacing(2),
    cursor: "pointer",
  },
  secondaryPopperAddress: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyItems: "center",
    maxWidth: 250,
  },
  addressInfo1: {
    fontSize: theme.fontSize.small,
    fontWeight: theme.fontWeight.bold,
    color: theme.palette.bluegrey.dark,
    maxWidth: 250,
  },
  addressInfo2: {
    fontWeight: theme.fontWeight.regular,
    fontSize: theme.fontSize.xsmall,
    color: theme.palette.bluegrey.dark,
  },
  popperTimeSection: {
    borderRadius: "0px 0px 1px 0px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: theme.palette.grey.light,
  },
  timeContainer: {
    height: 36,
  },
  flexRow: {
    display: "flex",
    gap: 4,
  },
  labelText: {
    fontSize: theme.fontSize.small,
    fontWeight: theme.fontWeight.regular,
    color: theme.palette.grey.medium,
  },
  statusText: {
    fontWeight: theme.fontWeight.bold,
    fontSize: theme.fontSize.small,
  },
  scheduledStartDateText: {
    fontWeight: theme.fontWeight.regular,
    fontSize: theme.fontSize.small,
    color: theme.palette.red.main,
    textDecoration: "line-through",
  },
  timeText: {
    fontSize: theme.fontSize.small,
  },
  requestedDateText: {
    fontSize: theme.fontSize.medium,
    fontWeight: theme.fontWeight.bold,
    color: theme.palette.grey.medium,
  },
  diffTotalTimeText: {
    fontSize: theme.fontSize.small,
  },
  checkListContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 8,
  },
  checkList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.grey.lightAccent,
  },
  checkListHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  issueStatus: {
    width: 78,
    height: 25,
    borderRadius: 4,
    fontWeight: theme.fontWeight.regular,
    fontSize: theme.fontSize.medium,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.common.white,
  },
  issueTitle: {
    fontWeight: theme.fontWeight.bold,
    fontSize: theme.fontSize.medium,
  },
  issueDescription: {
    fontWeight: theme.fontWeight.regular,
    fontSize: theme.fontSize.small,
  },
  attemptNo: {
    color: theme.palette.common.white,
    whiteSpace: "nowrap",
    backgroundColor: theme.palette.orange.main,
    display: "flex",
    alignItems: "center",
    border: `1px solid ${theme.palette.orange.dark}`,
    height: theme.spacing(4),
    borderRadius: theme.spacing(0.5),
    ":hover": {
      backgroundColor: theme.palette.orange.medium,
      textDecoration: "underline",
      cursor: "pointer",
    },
  },
  attemptStatusContainer: {
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
  },
  attemptStatus: {
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    height: theme.spacing(4),
    borderRadius: theme.spacing(0.5),
  },
}));
